import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add,
  CallOutlined,
  CheckCircleOutline,
  ContentCopyRounded,
  DeleteOutlineRounded,
  EditOutlined,
  Groups2Outlined,
  HeadsetMicOutlined,
  InfoOutlined,
  PhoneInTalkOutlined,
  PhoneMissedOutlined,
  SettingsOutlined,
  SupportAgentOutlined,
  WifiCallingOutlined,
} from "@mui/icons-material";
import {
  BRAND_PALETTE,
  TEAL_PALETTE,
  COLOR,
  CALL_STATUS,
  AGENT_STATUS,
  SECTION_STATUS,
  TYPE,
  SPACE,
  RADIUS,
  ANIM,
  DENSITY,
  FONT,
  monoSx,
  labelSx,
} from "../design-system/tokens";
import {
  StatusPill,
  StatCard,
  EmptyState,
  ConfirmDialog,
  PageSection,
} from "../design-system";
import { neuCardSx, neuSmCardSx } from "../styles/neu";

// ─── Section wrapper ──────────────────────────────────────────────────────────
function Section({ id, title, description, children }) {
  return (
    <Box
      id={id}
      component="section"
      sx={{ scrollMarginTop: "80px", mb: 7 }}
    >
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, letterSpacing: "-0.02em", mb: 0.5 }}
        >
          {title}
        </Typography>
        {description && (
          <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 600 }}>
            {description}
          </Typography>
        )}
        <Divider sx={{ mt: 2 }} />
      </Box>
      {children}
    </Box>
  );
}

// ─── Token swatch ─────────────────────────────────────────────────────────────
function Swatch({ name, value, textColor }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };
  return (
    <Tooltip title={copied ? "Copied!" : value} arrow placement="top">
      <Box
        onClick={copy}
        sx={{
          width: "100%",
          cursor: "pointer",
          borderRadius: RADIUS.md + "px",
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
          transition: "transform 120ms",
          "&:hover": { transform: "scale(1.03)" },
        }}
      >
        <Box sx={{ height: 56, bgcolor: value }} />
        <Box sx={{ px: "10px", py: "8px", bgcolor: "background.paper" }}>
          <Typography sx={{ fontSize: "0.6875rem", fontWeight: 700, color: "text.secondary" }}>
            {name}
          </Typography>
          <Typography sx={{ ...monoSx, fontSize: "0.6875rem", color: "text.disabled" }}>
            {value}
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  );
}

// ─── Token row ────────────────────────────────────────────────────────────────
function TokenRow({ name, value, preview }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "200px 1fr auto",
        gap: 2,
        alignItems: "center",
        py: 1,
        borderBottom: "1px solid",
        borderColor: "divider",
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <Typography sx={{ ...monoSx, fontSize: "0.8125rem", color: "text.secondary" }}>
        {name}
      </Typography>
      <Typography sx={{ ...monoSx, fontSize: "0.8125rem" }}>
        {value}
      </Typography>
      {preview}
    </Box>
  );
}

// ─── Label ────────────────────────────────────────────────────────────────────
function Label({ children, sx: sxProp }) {
  return (
    <Typography sx={{ ...labelSx, mb: 1.5, ...sxProp }}>
      {children}
    </Typography>
  );
}

// ─── Nav item ─────────────────────────────────────────────────────────────────
const NAV = [
  { id: "brand",      label: "Brand" },
  { id: "palette",    label: "Palette" },
  { id: "typography", label: "Typography" },
  { id: "spacing",    label: "Spacing" },
  { id: "motion",     label: "Motion" },
  { id: "status",     label: "Status Pills" },
  { id: "statcards",  label: "Stat Cards" },
  { id: "empty",      label: "Empty States" },
  { id: "confirm",    label: "Confirm Dialog" },
  { id: "sections",   label: "Page Section" },
  { id: "density",    label: "Density" },
  { id: "forms",      label: "Form Patterns" },
];

// ─────────────────────────────────────────────────────────────────────────────
export default function StyleGuide() {
  const [confirmOpen, setConfirmOpen]           = useState(false);
  const [confirmLoading, setConfirmLoading]     = useState(false);
  const [densityMode, setDensityMode]           = useState("guided");
  const [activeNav, setActiveNav]               = useState("brand");

  const handleConfirm = () => {
    setConfirmLoading(true);
    setTimeout(() => { setConfirmLoading(false); setConfirmOpen(false); }, 1800);
  };

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    setActiveNav(id);
  };

  const density = DENSITY[densityMode];

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>

      {/* ── Page header ─────────────────────────────────────────── */}
      <Box
        sx={{
          ...neuCardSx(false),
          borderRadius: 0,
          borderBottom: "1px solid",
          borderColor: "divider",
          px: 4,
          py: 3,
          position: "sticky",
          top: 0,
          zIndex: 15,
          backdropFilter: "blur(14px)",
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: "auto", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography
              variant="h5"
              sx={{ fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.2 }}
            >
              Teleredo Design System
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Living style guide · v1.0
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            {NAV.slice(0, 7).map(n => (
              <Button
                key={n.id}
                size="small"
                variant={activeNav === n.id ? "contained" : "text"}
                onClick={() => scrollTo(n.id)}
                sx={{ fontSize: "0.75rem", minWidth: 0, px: 1.5 }}
              >
                {n.label}
              </Button>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── Main content ────────────────────────────────────────── */}
      <Box sx={{ maxWidth: 1200, mx: "auto", px: { xs: 2, md: 4 }, py: 5 }}>

        {/* ── BRAND ──────────────────────────────────────────────── */}
        <Section
          id="brand"
          title="Brand Identity"
          description="The Teleredo brand is built on two anchors: deep purple authority and teal connectivity."
        >
          <Grid container spacing={3}>
            <Grid size={12}>
              <Box
                sx={{
                  ...neuCardSx(false),
                  borderRadius: RADIUS.xl + "px",
                  p: 4,
                  background: `linear-gradient(135deg, ${COLOR.brandPrimary} 0%, ${COLOR.brandDark} 60%, #14101c 100%)`,
                  color: COLOR.white,
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
                  gap: 4,
                }}
              >
                <Box>
                  <Typography sx={{ fontSize: "0.6875rem", fontWeight: 700, letterSpacing: "0.1em", opacity: 0.6, textTransform: "uppercase", mb: 1 }}>
                    Primary Brand
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 800, letterSpacing: "-0.03em", mb: 0.5 }}>
                    Deep Plum
                  </Typography>
                  <Typography sx={{ ...monoSx, opacity: 0.7, fontSize: "0.875rem" }}>
                    #7A3FF2
                  </Typography>
                  <Typography sx={{ mt: 2, opacity: 0.8, fontSize: "0.875rem", lineHeight: 1.65 }}>
                    Telecom authority without commodity blue. Warm enough to feel human, dark enough to anchor.
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "0.6875rem", fontWeight: 700, letterSpacing: "0.1em", opacity: 0.6, textTransform: "uppercase", mb: 1 }}>
                    Secondary Action
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 800, letterSpacing: "-0.03em", mb: 0.5, color: COLOR.tealPrimary }}>
                    Live Teal
                  </Typography>
                  <Typography sx={{ ...monoSx, opacity: 0.7, fontSize: "0.875rem" }}>
                    #06B6D4
                  </Typography>
                  <Typography sx={{ mt: 2, opacity: 0.8, fontSize: "0.875rem", lineHeight: 1.65 }}>
                    Connections are the product. Every call is a live link — teal signals activity, life, real-time.
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "0.6875rem", fontWeight: 700, letterSpacing: "0.1em", opacity: 0.6, textTransform: "uppercase", mb: 1 }}>
                    Surface Glass
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 800, letterSpacing: "-0.03em", mb: 0.5 }}>
                    Frosted
                  </Typography>
                  <Typography sx={{ ...monoSx, opacity: 0.7, fontSize: "0.875rem" }}>
                    backdrop-filter: blur
                  </Typography>
                  <Typography sx={{ mt: 2, opacity: 0.8, fontSize: "0.875rem", lineHeight: 1.65 }}>
                    Soft depth. Cards float above the gradient background without hard shadows that fatigue dense data views.
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Section>

        {/* ── PALETTE ────────────────────────────────────────────── */}
        <Section
          id="palette"
          title="Color Palette"
          description="Full color system — brand scales, semantic roles, and domain-specific status colors."
        >
          <Label>Brand Scale (Plum)</Label>
          <Grid container spacing={1.5} sx={{ mb: 4 }}>
            {Object.entries(BRAND_PALETTE).map(([k, v]) => (
              <Grid size={{ xs: 6, sm: 4, md: 2.4 }} key={k}>
                <Swatch name={`brand.${k}`} value={v} />
              </Grid>
            ))}
          </Grid>

          <Label>Teal Scale</Label>
          <Grid container spacing={1.5} sx={{ mb: 4 }}>
            {Object.entries(TEAL_PALETTE).map(([k, v]) => (
              <Grid size={{ xs: 6, sm: 4, md: 2.4 }} key={k}>
                <Swatch name={`teal.${k}`} value={v} />
              </Grid>
            ))}
          </Grid>

          <Label>Semantic Roles</Label>
          <Grid container spacing={1.5} sx={{ mb: 4 }}>
            {[
              { name: "success",  value: COLOR.success  },
              { name: "warning",  value: COLOR.warning  },
              { name: "danger",   value: COLOR.danger   },
              { name: "info",     value: COLOR.info     },
              { name: "neutral",  value: COLOR.neutral500 },
            ].map(s => (
              <Grid size={{ xs: 6, sm: 4, md: 2.4 }} key={s.name}>
                <Swatch name={s.name} value={s.value} />
              </Grid>
            ))}
          </Grid>

          <Label>Call Status Colors</Label>
          <Grid container spacing={1.5} sx={{ mb: 4 }}>
            {Object.entries(CALL_STATUS).map(([key, t]) => (
              <Grid size={{ xs: 6, sm: 4, md: 2 }} key={key}>
                <Swatch name={key} value={t.color} />
              </Grid>
            ))}
          </Grid>
        </Section>

        {/* ── TYPOGRAPHY ─────────────────────────────────────────── */}
        <Section
          id="typography"
          title="Typography"
          description={`Primary font: Inter (already loaded). Monospace for phone numbers, extensions, call IDs: ${FONT.mono}`}
        >
          <Box sx={{ ...neuCardSx(false), borderRadius: RADIUS.lg + "px", overflow: "hidden" }}>
            {Object.entries(TYPE).map(([name, t], i, arr) => (
              <Box
                key={name}
                sx={{
                  px: 3,
                  py: 2,
                  borderBottom: i < arr.length - 1 ? "1px solid" : "none",
                  borderColor: "divider",
                  display: "flex",
                  alignItems: "baseline",
                  gap: 3,
                }}
              >
                <Box sx={{ width: 80, flexShrink: 0 }}>
                  <Typography sx={{ ...monoSx, fontSize: "0.75rem", color: "text.disabled" }}>
                    {name}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: t.family ?? FONT.sans,
                      fontSize:   t.size,
                      fontWeight: t.weight,
                      lineHeight: t.leading,
                      letterSpacing: t.tracking ?? "normal",
                      textTransform: t.upper ? "uppercase" : "none",
                    }}
                  >
                    {name === "label" ? "OVERLINE LABEL" :
                     name === "display" ? "Display Hero" :
                     name === "mono" || name === "monoSm" ? "+972 50-123-4567 · ext. 3042" :
                     "The quick brown fox"}
                  </Typography>
                </Box>
                <Box sx={{ width: 180, flexShrink: 0, textAlign: "right" }}>
                  <Typography sx={{ ...monoSx, fontSize: "0.75rem", color: "text.disabled" }}>
                    {t.size} / {t.weight}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Section>

        {/* ── SPACING & RADIUS ───────────────────────────────────── */}
        <Section
          id="spacing"
          title="Spacing & Radius"
          description="4px base spacing scale. All border-radius values in pixels."
        >
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Label>Spacing Scale</Label>
              <Box sx={{ ...neuCardSx(false), borderRadius: RADIUS.lg + "px", p: 2 }}>
                {[1, 2, 3, 4, 6, 8, 10, 12, 16].map(n => (
                  <Box key={n} sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
                    <Typography sx={{ ...monoSx, fontSize: "0.75rem", color: "text.disabled", width: 40 }}>
                      {n}
                    </Typography>
                    <Box sx={{
                      height: 8,
                      width: parseInt(SPACE[n]),
                      bgcolor: COLOR.tealPrimary,
                      borderRadius: 1,
                      minWidth: 4,
                    }} />
                    <Typography sx={{ ...monoSx, fontSize: "0.75rem", color: "text.secondary" }}>
                      {SPACE[n]}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Label>Border Radius</Label>
              <Box sx={{ ...neuCardSx(false), borderRadius: RADIUS.lg + "px", p: 2 }}>
                {Object.entries(RADIUS).filter(([k]) => k !== "full").map(([name, val]) => (
                  <Box key={name} sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.5 }}>
                    <Typography sx={{ ...monoSx, fontSize: "0.75rem", color: "text.disabled", width: 40 }}>
                      {name}
                    </Typography>
                    <Box sx={{
                      width: 56,
                      height: 28,
                      border: `2px solid ${COLOR.brandPrimary}`,
                      borderRadius: val + "px",
                      bgcolor: COLOR.brandSubtle,
                    }} />
                    <Typography sx={{ ...monoSx, fontSize: "0.75rem", color: "text.secondary" }}>
                      {val}px
                    </Typography>
                  </Box>
                ))}
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Typography sx={{ ...monoSx, fontSize: "0.75rem", color: "text.disabled", width: 40 }}>
                    full
                  </Typography>
                  <Box sx={{
                    width: 56,
                    height: 28,
                    border: `2px solid ${COLOR.brandPrimary}`,
                    borderRadius: "9999px",
                    bgcolor: COLOR.brandSubtle,
                  }} />
                  <Typography sx={{ ...monoSx, fontSize: "0.75rem", color: "text.secondary" }}>
                    9999px
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Section>

        {/* ── MOTION ─────────────────────────────────────────────── */}
        <Section
          id="motion"
          title="Motion"
          description="Speed and easing tokens. Use fast for micro-interactions, normal for transitions, slow for large surface changes."
        >
          <Grid container spacing={2}>
            {[
              { name: "Fast",   value: ANIM.durationFast,   ease: ANIM.easeOut, ms: 120 },
              { name: "Normal", value: ANIM.durationNormal, ease: ANIM.easeOut, ms: 180 },
              { name: "Slow",   value: ANIM.durationSlow,   ease: ANIM.easeOut, ms: 280 },
            ].map(t => (
              <Grid size={{ xs: 12, sm: 4 }} key={t.name}>
                <Box
                  sx={{
                    ...neuSmCardSx(false),
                    borderRadius: RADIUS.lg + "px",
                    p: "20px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: "0.9375rem" }}>{t.name}</Typography>
                  <Box
                    sx={{
                      height: 6,
                      bgcolor: COLOR.tealSubtle,
                      borderRadius: 99,
                      overflow: "hidden",
                      position: "relative",
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        left: 0,
                        top: 0,
                        height: "100%",
                        width: "100%",
                        bgcolor: COLOR.tealPrimary,
                        borderRadius: 99,
                        animation: `slideIn ${t.ms * 2}ms ${t.ease} infinite alternate`,
                        "@keyframes slideIn": {
                          "0%":   { transform: "translateX(-100%)" },
                          "100%": { transform: "translateX(0%)" },
                        },
                      },
                    }}
                  />
                  <Typography sx={{ ...monoSx, fontSize: "0.75rem", color: "text.secondary" }}>
                    {t.value} · easeOut
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Section>

        {/* ── STATUS PILLS ───────────────────────────────────────── */}
        <Section
          id="status"
          title="Status Pills"
          description="Call disposition, agent availability, and section state indicators. All contrast-AA accessible."
        >
          <Label>Call Dispositions</Label>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 3 }}>
            {Object.keys(CALL_STATUS).map(key => (
              <Box key={key}>
                <StatusPill status={key} domain="call" tooltip={false} />
              </Box>
            ))}
          </Box>

          <Label>Dot variant (compact rows)</Label>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5, mb: 3 }}>
            {Object.keys(CALL_STATUS).map(key => (
              <StatusPill key={key} status={key} domain="call" dot tooltip={false} />
            ))}
          </Box>

          <Label>Agent Availability</Label>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 3 }}>
            {Object.keys(AGENT_STATUS).map(key => (
              <StatusPill key={key} status={key} domain="agent" tooltip={false} />
            ))}
          </Box>

          <Label>Section States</Label>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
            {Object.keys(SECTION_STATUS).map(key => (
              <StatusPill key={key} status={key} domain="section" tooltip={false} />
            ))}
          </Box>
        </Section>

        {/* ── STAT CARDS ─────────────────────────────────────────── */}
        <Section
          id="statcards"
          title="Stat Cards"
          description="Dashboard metric tiles. Trend arrows automatically flip for inverted metrics (e.g. missed calls — fewer is better)."
        >
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                label="Answered Today"
                value={142}
                unit="calls"
                icon={<CallOutlined />}
                trend={12}
                trendLabel="vs yesterday"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                label="Answer Rate"
                value="91.4"
                unit="%"
                icon={<CheckCircleOutline />}
                iconColor={COLOR.success}
                trend={3.2}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                label="Missed Calls"
                value={13}
                unit="calls"
                icon={<PhoneMissedOutlined />}
                iconColor={COLOR.danger}
                trend={-5}
                trendInvert
                trendLabel="vs yesterday"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                label="Live in Queue"
                value={3}
                unit="waiting"
                icon={<PhoneInTalkOutlined />}
                iconColor={COLOR.tealPrimary}
              />
            </Grid>
          </Grid>

          <Label sx={{ mt: 3 }}>Compact (Operational Density)</Label>
          <Grid container spacing={2}>
            {[
              { label: "Active Agents", value: 8, icon: <HeadsetMicOutlined />, trend: 0, unit: "online" },
              { label: "Avg Handle Time", value: "4:32", unit: "min", icon: <WifiCallingOutlined />, iconColor: COLOR.info, trend: -8, trendInvert: true },
              { label: "SLA Compliance", value: "94.2", unit: "%", icon: <CheckCircleOutline />, iconColor: COLOR.success, trend: 1.7 },
            ].map((c, i) => (
              <Grid size={{ xs: 12, sm: 4 }} key={i}>
                <StatCard {...c} compact />
              </Grid>
            ))}
          </Grid>

          <Label sx={{ mt: 3 }}>Loading skeleton</Label>
          <Grid container spacing={2.5}>
            {[0, 1, 2, 3].map(i => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
                <StatCard loading label="—" value="—" />
              </Grid>
            ))}
          </Grid>
        </Section>

        {/* ── EMPTY STATES ───────────────────────────────────────── */}
        <Section
          id="empty"
          title="Empty States"
          description="Consistent zero-data treatment across list views, tables, and full-page contexts."
        >
          <Grid container spacing={2.5}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Box sx={{ ...neuCardSx(false), borderRadius: RADIUS.lg + "px", overflow: "hidden" }}>
                <EmptyState
                  size="sm"
                  inline
                  icon={<CallOutlined />}
                  title="No calls today"
                  description="Calls will appear here once your call center is live."
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Box sx={{ ...neuCardSx(false), borderRadius: RADIUS.lg + "px", overflow: "hidden" }}>
                <EmptyState
                  icon={<Groups2Outlined />}
                  title="No agents configured"
                  description="Add SIP extensions to start routing calls to your team."
                  action={{ label: "Add Agent", onClick: () => {}, startIcon: <Add /> }}
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Box sx={{ ...neuCardSx(false), borderRadius: RADIUS.lg + "px", overflow: "hidden" }}>
                <EmptyState
                  size="lg"
                  icon={<SupportAgentOutlined />}
                  title="No sections yet"
                  description="Create your first department to start building your IVR call flow. Each section can have its own routing strategy, agents, and schedule."
                  action={{ label: "Create Section", onClick: () => {}, startIcon: <Add /> }}
                  secondaryAction={{ label: "View documentation", onClick: () => {} }}
                />
              </Box>
            </Grid>
          </Grid>
        </Section>

        {/* ── CONFIRM DIALOG ─────────────────────────────────────── */}
        <Section
          id="confirm"
          title="Confirm Dialog"
          description="Destructive action gate. Cancel is auto-focused to prevent accidental confirmation. Loading state prevents double-submit."
        >
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteOutlineRounded />}
              onClick={() => setConfirmOpen(true)}
            >
              Delete section (demo)
            </Button>
          </Box>

          <ConfirmDialog
            open={confirmOpen}
            onClose={() => setConfirmOpen(false)}
            onConfirm={handleConfirm}
            loading={confirmLoading}
            title="Delete Sales Section?"
            description="This will permanently remove the Sales section, all its routing rules, agent assignments, and queue settings. Calls will no longer route to this department."
            confirmLabel="Delete section"
            severity="danger"
          />
        </Section>

        {/* ── PAGE SECTION ───────────────────────────────────────── */}
        <Section
          id="sections"
          title="Page Section"
          description="Glass-surface content wrapper. Handles header + divider + body padding uniformly."
        >
          <Stack spacing={2.5}>
            <PageSection title="Recent Calls" action={<Button size="small" variant="outlined">Export</Button>}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Table or list content goes here.
              </Typography>
            </PageSection>

            <PageSection
              title="Queue Configuration"
              subtitle="Settings that control how callers wait in the Sales queue"
              action={<Button size="small" startIcon={<EditOutlined />}>Edit</Button>}
            >
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Queue settings content.
              </Typography>
            </PageSection>

            <PageSection noPad title="Agents">
              <Box sx={{ px: 3, py: 1.5, bgcolor: "action.hover", borderRadius: `0 0 ${RADIUS.lg}px ${RADIUS.lg}px` }}>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  noPad — table extends edge-to-edge after the header.
                </Typography>
              </Box>
            </PageSection>
          </Stack>
        </Section>

        {/* ── DENSITY MODES ──────────────────────────────────────── */}
        <Section
          id="density"
          title="Density Modes"
          description="Two density modes for two audiences. Operational = agents/supervisors (data-dense, hours of use). Guided = admins/owners (spacious, configuration-focused)."
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
            <Typography sx={{ fontWeight: 600, fontSize: "0.9375rem" }}>Operational</Typography>
            <Switch
              checked={densityMode === "guided"}
              onChange={e => setDensityMode(e.target.checked ? "guided" : "operational")}
            />
            <Typography sx={{ fontWeight: 600, fontSize: "0.9375rem" }}>Guided</Typography>
          </Box>

          <Grid container spacing={2.5}>
            {/* Fake table */}
            <Grid size={{ xs: 12, md: 8 }}>
              <Box sx={{ ...neuCardSx(false), borderRadius: RADIUS.lg + "px", overflow: "hidden" }}>
                <Box sx={{ p: density.cardPadding, borderBottom: "1px solid", borderColor: "divider" }}>
                  <Typography sx={{ fontWeight: 700, fontSize: density.bodySize }}>
                    Agent Status
                  </Typography>
                </Box>
                {["Sarah Al-Ahmad", "Omar Khalid", "Lina Natour"].map((name, i) => (
                  <Box
                    key={name}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr auto auto",
                      alignItems: "center",
                      p: density.cellPadding,
                      gap: 2,
                      height: density.rowHeight,
                      borderBottom: i < 2 ? "1px solid" : "none",
                      borderColor: "divider",
                    }}
                  >
                    <Typography sx={{ fontSize: density.bodySize, fontWeight: 500 }}>
                      {name}
                    </Typography>
                    <Typography sx={{ ...monoSx, fontSize: density.captionSize, color: "text.disabled" }}>
                      ext. {3040 + i}
                    </Typography>
                    <StatusPill
                      status={["AVAILABLE", "ON_CALL", "AWAY"][i]}
                      domain="agent"
                      size={density.buttonSize === "small" ? "sm" : "md"}
                      tooltip={false}
                    />
                  </Box>
                ))}
              </Box>
            </Grid>

            {/* Fake form */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Box
                sx={{
                  ...neuCardSx(false),
                  borderRadius: RADIUS.lg + "px",
                  p: density.cardPadding,
                  display: "flex",
                  flexDirection: "column",
                  gap: `${density.sectionGap}px`,
                }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: density.bodySize }}>
                  Quick Settings
                </Typography>
                <TextField
                  label="Ring Timeout"
                  defaultValue="20"
                  size={density.inputSize}
                  fullWidth
                />
                <TextField
                  label="Max Queue Wait"
                  defaultValue="120"
                  size={density.inputSize}
                  fullWidth
                />
                <Button
                  variant="contained"
                  size={density.buttonSize}
                  fullWidth
                >
                  Save
                </Button>
              </Box>
            </Grid>
          </Grid>

          <Alert severity="info" sx={{ mt: 2, borderRadius: RADIUS.md + "px" }}>
            <strong>How to apply:</strong> Read <code>DENSITY[mode]</code> values and pass them as inline sx or props.
            The active mode is stored in user preferences (Redux profile). Toggle above simulates both.
          </Alert>
        </Section>

        {/* ── FORM PATTERNS ──────────────────────────────────────── */}
        <Section
          id="forms"
          title="Form Patterns"
          description="Standard patterns for the two main form types in the product."
        >
          <Grid container spacing={3}>
            {/* Config form */}
            <Grid size={{ xs: 12, md: 6 }}>
              <PageSection title="Queue Settings" subtitle="Guided admin form pattern">
                <Stack spacing={2.5}>
                  <TextField label="Strategy" select defaultValue="rrmemory" size="medium" fullWidth
                    SelectProps={{ native: true }}>
                    <option value="rrmemory">Round Robin (Memory)</option>
                    <option value="ringall">Ring All</option>
                    <option value="leastrecent">Least Recent</option>
                  </TextField>
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                    <TextField label="Max Wait (s)" defaultValue="120" size="medium" />
                    <TextField label="Ring Time (s)" defaultValue="20"  size="medium" />
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, pt: 1 }}>
                    <Button variant="outlined" size="medium">Cancel</Button>
                    <Button variant="contained" size="medium">Save Changes</Button>
                  </Box>
                </Stack>
              </PageSection>
            </Grid>

            {/* Operational action bar */}
            <Grid size={{ xs: 12, md: 6 }}>
              <PageSection title="Operational Actions" subtitle="Compact agent/supervisor controls">
                <Stack spacing={1.5}>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Button size="small" variant="contained" startIcon={<CallOutlined />}>
                      Barge In
                    </Button>
                    <Button size="small" variant="outlined">Listen</Button>
                    <Button size="small" variant="outlined">Whisper</Button>
                    <Button size="small" variant="outlined" color="error" startIcon={<PhoneMissedOutlined />}>
                      Kick
                    </Button>
                  </Box>
                  <Divider />
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Chip label="Queue: Sales" size="small" />
                    <Chip label="Wait: 2:14" size="small" icon={<WifiCallingOutlined sx={{ fontSize: 14 }} />} />
                    <StatusPill status="LIVE" domain="call" size="sm" tooltip={false} />
                  </Box>
                  <Alert severity="warning" sx={{ fontSize: "0.8125rem", py: 0.5 }}>
                    Destructive actions (Kick, Barge) require confirming via ConfirmDialog.
                  </Alert>
                </Stack>
              </PageSection>
            </Grid>
          </Grid>
        </Section>

        {/* ── Footer ─────────────────────────────────────────────── */}
        <Box sx={{ textAlign: "center", py: 6, color: "text.disabled" }}>
          <Typography variant="caption">
            Teleredo Design System · Built on MUI v7 + Emotion · Inter + glassmorphism tokens
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
